import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Input, Select, Option,
    IconButton, Chip, Divider, CircularProgress,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon     from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon           from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon        from '@mui/icons-material/DeleteRounded';
import SendRoundedIcon          from '@mui/icons-material/SendRounded';
import CreditCardRoundedIcon    from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon      from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon   from '@mui/icons-material/PointOfSaleRounded';

import UndoToast from '../../components/UndoToast';
import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APICreateOrdersBatch, APIDeleteOrder,
    APIGetEmployees, APIGetLocations,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const MONEY_TYPES = [
    { value: 'transfer', label: 'Перевод',   icon: <CreditCardRoundedIcon sx={{ fontSize: 16 }} />, color: '#818cf8', bg: 'rgba(129,140,248,0.12)' },
    { value: 'cash',     label: 'Наличные',  icon: <PaymentsRoundedIcon   sx={{ fontSize: 16 }} />, color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
    { value: 'terminal', label: 'Терминал',  icon: <PointOfSaleRoundedIcon sx={{ fontSize: 16 }} />, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
];

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const itemAnim = {
    initial: { opacity: 0, x: -12 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.25 } },
    exit:    { opacity: 0, x: 12, transition: { duration: 0.18 } },
};


// ════════════════════════════════════════════════════════════════════
//  AddOrderPage
// ════════════════════════════════════════════════════════════════════
export default function AddOrderPage() {
    const { token, showToast, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canCreate  = hasPerm('finance.orders.create');
    const canSetDate = hasPerm('finance.orders.set_date');

    // ── Справочники ───────────────────────────────────────────────
    const [employees,  setEmployees]  = useState([]);
    const [locations,  setLocations]  = useState([]);
    const [loadingMeta, setLoadingMeta] = useState(true);

    // ── Форма ─────────────────────────────────────────────────────
    const [photographerId, setPhotographerId] = useState('');
    const [locationId,     setLocationId]     = useState('');
    const [administratorId, setAdministratorId] = useState('');
    const [date,           setDate]           = useState(() => toISO(new Date()));
    const [items,          setItems]          = useState([
        { id: 1, value: '', money_type: 'transfer' },
    ]);
    const [nextItemId,     setNextItemId]     = useState(2);
    const [submitting,     setSubmitting]     = useState(false);
    const [undoData, setUndoData] = useState(null); // { ids: [...], count, sum }
    const [mode, setMode] = useState('different');  // 'same' | 'different'
    const [sameCount, setSameCount] = useState(2);
    const [sameValue, setSameValue] = useState('');
    const [sameMoneyType, setSameMoneyType] = useState('transfer');

    // ── Загрузка справочников ─────────────────────────────────────
    useEffect(() => {
        (async () => {
            const [empl, locs] = await Promise.all([
                APIGetEmployees(token, 'photographer'),
                APIGetLocations(token),
            ]);
            if (Array.isArray(empl)) setEmployees(empl);
            if (Array.isArray(locs)) {
                setLocations(locs);
                if (locs.length === 1) setLocationId(locs[0].id);
            }
            setLoadingMeta(false);
        })();
    }, [token]);

    // ── Управление строками ───────────────────────────────────────
    const addItem = () => {
        setItems(prev => [...prev, { id: nextItemId, value: '', money_type: 'transfer' }]);
        setNextItemId(p => p + 1);
    };

    const removeItem = (id) => {
        setItems(prev => prev.length > 1 ? prev.filter(i => i.id !== id) : prev);
    };

    const updateItem = (id, field, val) => {
        setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: val } : i));
    };

    // ── Итого ─────────────────────────────────────────────────────
    const totalSum = mode === 'same'
        ? (parseFloat(sameValue) || 0) * sameCount
        : items.reduce((s, i) => s + (parseFloat(i.value) || 0), 0);

    const totalCount = mode === 'same'
        ? sameCount
        : items.filter(i => parseFloat(i.value) > 0).length;

    // ── Отправка ──────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!photographerId) { showToast('Выберите фотографа', 'warning'); return; }
        if (!locationId)     { showToast('Выберите точку', 'warning'); return; }
        if (canSetDate && !date) { showToast('Укажите дату', 'warning'); return; }

        // Собираем items в зависимости от режима
        let payloadItems;
        if (mode === 'same') {
            const v = parseFloat(sameValue);
            if (!v || v <= 0) { showToast('Укажите сумму чека', 'warning'); return; }
            if (sameCount < 1) { showToast('Укажите количество', 'warning'); return; }
            payloadItems = Array.from({ length: sameCount }, () => ({
                value: v,
                money_type: sameMoneyType,
            }));
        } else {
            payloadItems = items
                .filter(i => parseFloat(i.value) > 0)
                .map(i => ({ value: parseFloat(i.value), money_type: i.money_type }));
            if (payloadItems.length === 0) { showToast('Добавьте хотя бы один чек с суммой', 'warning'); return; }
        }

        setSubmitting(true);
        const payload = {
            photographer_id:  photographerId,
            location_id:      locationId,
            administrator_id: administratorId || null,
            items: payloadItems,
        };
        if (canSetDate && date) {
            payload.date = date;
        }

        const res = await APICreateOrdersBatch(token, payload);
        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            const ids = Array.isArray(res) ? res.map(o => o.id) : [];
            setUndoData({
                ids,
                message: `Добавлено ${ids.length} чек. на ${fmtMoney(totalSum)} ₽`,
            });
        }
        setSubmitting(false);
    };

    // Undo — удалить только что созданные чеки
    const handleUndo = async () => {
        if (!undoData?.ids?.length) return;
        for (const id of undoData.ids) {
            await APIDeleteOrder(token, id);
        }
        showToast('Чеки отменены');
        setUndoData(null);
    };

    // Перейти без отмены
    const handleConfirmUndo = () => {
        setUndoData(null);
        navigate('/manager/orders');
    };

    // ── Рендер ────────────────────────────────────────────────────
    if (loadingMeta) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'var(--joy-palette-background-body)' }}>
                <MemeLoader />
            </Box>
        );
    }

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ─────────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <IconButton variant="plain" onClick={() => navigate('/manager/orders')}
                        sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                            Добавить чеки
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            Пакетный ввод продаж
                        </Typography>
                    </Box>
                </Box>

                {/* ── Форма ─────────────────────────────────────── */}
                <Box sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    p: { xs: 2, sm: 2.5 },
                }}>
                    <Stack spacing={2}>

                        {/* Фотограф */}
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Фотограф
                            </Typography>
                            <Select
                                value={photographerId}
                                onChange={(_, v) => setPhotographerId(v || '')}
                                placeholder="Выберите фотографа"
                                size="sm"
                            >
                                {employees.map(e => (
                                    <Option key={e.id} value={e.id}>
                                        {e.first_name} {e.last_name}
                                    </Option>
                                ))}
                            </Select>
                        </Box>

                        {/* Точка + Дата */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Точка
                                </Typography>
                                <Select
                                    value={locationId}
                                    onChange={(_, v) => setLocationId(v || '')}
                                    placeholder="Точка"
                                    size="sm"
                                >
                                    {locations.map(l => (
                                        <Option key={l.id} value={l.id}>{l.name}</Option>
                                    ))}
                                </Select>
                            </Box>
                            {canSetDate && (
                                <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Дата
                                    </Typography>
                                    <Input
                                        type="date" size="sm"
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Администратор (опц.) */}
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Администратор <span style={{ fontWeight: 400, opacity: 0.6 }}>(необязательно)</span>
                            </Typography>
                            <Select
                                value={administratorId}
                                onChange={(_, v) => setAdministratorId(v || '')}
                                placeholder="Без администратора"
                                size="sm"
                            >
                                <Option value="">Без администратора</Option>
                                {employees.map(e => (
                                    <Option key={e.id} value={e.id}>
                                        {e.first_name} {e.last_name}
                                    </Option>
                                ))}
                            </Select>
                        </Box>

                        <Divider sx={{ my: 0.5 }} />

                        {/* ── Режим ввода ──────────────────────────── */}
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Режим ввода
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {[
                                    { value: 'same',      label: 'Одинаковые суммы' },
                                    { value: 'different', label: 'Разные суммы' },
                                ].map(m => (
                                    <Chip key={m.value} size="md"
                                        variant={mode === m.value ? 'solid' : 'soft'}
                                        color={mode === m.value ? 'primary' : 'neutral'}
                                        onClick={() => setMode(m.value)}
                                        sx={{
                                            flex: 1, justifyContent: 'center',
                                            cursor: 'pointer', fontWeight: 700, fontSize: 12,
                                            transition: 'all .15s',
                                        }}
                                    >
                                        {m.label}
                                    </Chip>
                                ))}
                            </Box>
                        </Box>

                        <Divider sx={{ my: 0.5 }} />

                        {/* ── Одинаковые суммы ─────────────────────── */}
                        {mode === 'same' && (
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Чеки
                                </Typography>
                                <Stack spacing={1.5}>
                                    {/* Количество */}
                                    <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                        <Box>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mb: 0.3 }}>Количество</Typography>
                                            <Input type="number" size="sm" value={sameCount}
                                                onChange={(e) => setSameCount(Math.max(1, parseInt(e.target.value) || 1))}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mb: 0.3 }}>Сумма каждого</Typography>
                                            <Input type="number" size="sm" placeholder="0" value={sameValue}
                                                onChange={(e) => setSameValue(e.target.value)}
                                                endDecorator={<Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>₽</Typography>}
                                            />
                                        </Box>
                                    </Stack>

                                    {/* Тип оплаты */}
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mb: 0.3 }}>Тип оплаты (для всех)</Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {MONEY_TYPES.map(mt => {
                                                const active = sameMoneyType === mt.value;
                                                return (
                                                    <Chip key={mt.value} size="md"
                                                        variant={active ? 'solid' : 'outlined'}
                                                        onClick={() => setSameMoneyType(mt.value)}
                                                        startDecorator={mt.icon}
                                                        sx={{
                                                            flex: 1, justifyContent: 'center',
                                                            cursor: 'pointer', fontWeight: 700, fontSize: 11,
                                                            bgcolor: active ? mt.bg : 'transparent',
                                                            color: active ? mt.color : 'var(--joy-palette-text-tertiary)',
                                                            borderColor: active ? mt.color : '#3a3a5a',
                                                            transition: 'all .15s',
                                                            '&:hover': { borderColor: mt.color },
                                                        }}
                                                    >
                                                        {mt.label}
                                                    </Chip>
                                                );
                                            })}
                                        </Box>
                                    </Box>

                                    {/* Превью */}
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        borderRadius: 'var(--joy-radius-lg)',
                                        p: 1.5,
                                    }}>
                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                            Будет создано {sameCount} чек. по {fmtMoney(sameValue || 0)} ₽ = {fmtMoney(totalSum)} ₽
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        )}

                        {/* ── Разные суммы (текущая логика) ────────── */}
                        {mode === 'different' && (
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Чеки
                                    </Typography>
                                    <Button size="sm" variant="soft"
                                        startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                        onClick={addItem}
                                        sx={{ fontSize: 11, fontWeight: 700 }}
                                    >
                                        Добавить строку
                                    </Button>
                                </Box>

                                <Stack spacing={1}>
                                    <AnimatePresence>
                                        {items.map((item) => (
                                            <motion.div key={item.id} {...itemAnim} layout>
                                                <Box sx={{
                                                    display: 'flex', gap: 1, alignItems: 'center',
                                                    bgcolor: 'var(--joy-palette-background-level2)',
                                                    borderRadius: 'var(--joy-radius-lg)',
                                                    p: 1,
                                                }}>
                                                    <Input type="number" placeholder="Сумма" size="sm"
                                                        value={item.value}
                                                        onChange={(e) => updateItem(item.id, 'value', e.target.value)}
                                                        endDecorator={<Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>₽</Typography>}
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                        {MONEY_TYPES.map(mt => {
                                                            const active = item.money_type === mt.value;
                                                            return (
                                                                <Chip key={mt.value} size="md"
                                                                    variant={active ? 'solid' : 'outlined'}
                                                                    onClick={() => updateItem(item.id, 'money_type', mt.value)}
                                                                    sx={{
                                                                        cursor: 'pointer', fontWeight: 700, fontSize: 10,
                                                                        px: 2, py: 0.5,
                                                                        bgcolor: active ? mt.bg : 'transparent',
                                                                        color: active ? mt.color : 'var(--joy-palette-text-tertiary)',
                                                                        borderColor: active ? mt.color : 'var(--joy-palette-background-level2)',
                                                                        transition: 'all .15s',
                                                                        '&:hover': { borderColor: mt.color },
                                                                    }}
                                                                >
                                                                    {mt.icon}
                                                                </Chip>
                                                            );
                                                        })}
                                                    </Box>
                                                    <IconButton size="sm" variant="plain"
                                                        onClick={() => removeItem(item.id)}
                                                        disabled={items.length === 1}
                                                        sx={{
                                                            color: 'var(--joy-palette-text-tertiary)',
                                                            '&:hover': { color: 'var(--joy-palette-danger-400)' },
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <DeleteRoundedIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Box>
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </Stack>
                            </Box>
                        )}

                        <Divider sx={{ my: 0.5 }} />

                        {/* ── Итого + кнопка ──────────────────────── */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                    Итого: {totalCount} чек.
                                </Typography>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'var(--joy-palette-primary-300)' }}>
                                    {fmtMoney(totalSum)} ₽
                                </Typography>
                            </Box>
                            <Button size="lg" loading={submitting}
                                disabled={!canCreate || !!undoData}
                                startDecorator={<SendRoundedIcon sx={{ fontSize: 18 }} />}
                                onClick={handleSubmit}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: 14, border: 'none',
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                    '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                    px: 3,
                                }}
                            >
                                Сохранить
                            </Button>
                        </Box>
                    </Stack>
                </Box>

                {/* ── UndoToast ──────────────────────────── */}
                <UndoToast
                    visible={!!undoData}
                    message={undoData?.message || ''}
                    timeout={10000}
                    onUndo={handleUndo}
                    onExpire={handleConfirmUndo}
                />
            </Box>
        </motion.div>
    );
}